import { useId, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useT } from '../i18n/i18n';
import { SESSION_STATUS_LABEL, itemLabel, resultText, type Session, type SessionStatus } from './api';
import { parseRepeat, shortTime, useDates } from './dates';
import DateField from '../components/DateField';

export const STATUS_ICON: Record<SessionStatus, string> = { DONE: '✓', PARTLY: '◐', MISSED: '✕', PLANNED: '○' };

export function StatusWord({ status }: { status: SessionStatus }) {
  const t = useT();
  return (
    <span className={`planner-status is-${status.toLowerCase()}`}>
      <span aria-hidden="true" className="planner-status-icon">{STATUS_ICON[status]}</span>
      {t(SESSION_STATUS_LABEL[status])}
    </span>
  );
}

export function CommentCount({ n }: { n: number }) {
  const t = useT();
  if (n <= 0) return null;
  return (
    <span className="planner-comment-count">
      <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14"><path d="M2 3h12v8H7l-3 3v-3H2z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
      <span aria-hidden="true">{n}</span>
      <span className="visually-hidden">{n === 1 ? t('1 comment') : t('{n} comments', { n })}</span>
    </span>
  );
}

export function useRepeatText() {
  const t = useT();
  const d = useDates();
  return (days: number[], until: string | null) => {
    if (days.length === 0) return t('Does not repeat');
    return until
      ? t('Every {days} until {date}', { days: d.dayList(days), date: d.short(until) })
      : t('Every {days}', { days: d.dayList(days) });
  };
}

export function sessionRepeat(s: Session, text: (days: number[], until: string | null) => string): string | null {
  const r = parseRepeat(s.repeat);
  return r ? text(r.days, r.until) : null;
}

export function sessionName(s: Session, fallback: string): string {
  if (s.title) return s.title;
  const names = s.items.map(itemLabel).filter(Boolean);
  if (names.length === 0) return fallback;
  return names.slice(0, 2).join(', ') + (names.length > 2 ? ' …' : '');
}

export function sessionScore(s: Session): string | null {
  if (s.status !== 'DONE' && s.status !== 'PARTLY') return null;
  const scores = s.items.map(resultText).filter((x): x is string => !!x);
  return scores.length ? scores.slice(0, 2).join(' · ') : null;
}

export function MoveForm({ session, onMove, compact }: { session: Session; onMove: (s: Session, day: string) => Promise<void> | void; compact?: boolean }) {
  const t = useT();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(session.day);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return <button type="button" className="btn secondary" onClick={() => { setDay(session.day); setOpen(true); }}>{t('Move to…')}</button>;
  }
  return (
    <div className={compact ? 'planner-move is-compact' : 'planner-move'}>
      <div className="field">
        <label htmlFor={`${id}-day`}>{t('New date')}</label>
        <DateField id={`${id}-day`} value={day} onChange={(v) => setDay(v ?? '')} />
      </div>
      <div className="toolbar">
        <button
          type="button"
          className="btn"
          disabled={busy || !day || day === session.day}
          onClick={async () => {
            setBusy(true);
            try {
              await onMove(session, day);
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('Move')}
        </button>
        <button type="button" className="btn ghost" onClick={() => setOpen(false)}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

export function SessionDetails({ session }: { session: Session }) {
  const t = useT();
  const d = useDates();
  const repeatText = useRepeatText();
  const time = shortTime(session.startTime);
  const repeat = sessionRepeat(session, repeatText);
  return (
    <>
      <p className="planner-when">
        {d.long(session.day)}
        {time && <> · {time}</>}
        {session.minutes != null && <> · {t('{n} minutes', { n: session.minutes })}</>}
      </p>
      {session.who && <p className="planner-meta">{t('Run by {who}', { who: session.who })}</p>}
      {repeat && <p className="planner-meta">{repeat}</p>}
      {session.items.length > 0 && (
        <ul className="planner-item-summary">
          {session.items.map((i) => {
            const r = resultText(i);
            return (
              <li key={i.id}>
                <span className="planner-item-name">{itemLabel(i)}</span>
                {i.targetStatement && <span className="planner-meta">{t('Target: {target}', { target: i.targetStatement })}</span>}
                {r && <span className="planner-meta">{t('Result: {result}', { result: r })}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {session.latestComment && (
        <p className="planner-latest-comment">
          <strong>{t('Latest comment from {name}:', { name: session.latestComment.authorName ?? t('Someone') })}</strong> {session.latestComment.text}
        </p>
      )}
    </>
  );
}

type ChipProps = {
  session: Session;
  onOpen: (s: Session) => void;
  onMove: (s: Session, day: string) => Promise<void> | void;
  draggable?: boolean;
  tabIndex?: number;
  align?: 'start' | 'end';
  wide?: boolean;
  onKey?: (e: KeyboardEvent<HTMLButtonElement>) => void;
};

export function SessionChip({ session, onOpen, onMove, draggable, tabIndex, align = 'start', wide, onKey }: ChipProps) {
  const t = useT();
  const cardId = useId();
  const [shown, setShown] = useState(false);
  const pointer = useRef<string>('mouse');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const time = shortTime(session.startTime);
  const score = sessionScore(session);

  function onDragStart(e: DragEvent<HTMLButtonElement>) {
    setShown(false);
    e.dataTransfer.setData('text/plain', session.id);
    e.dataTransfer.effectAllowed = 'move';
  }

  return (
    <div
      ref={wrapRef}
      className={`planner-session-wrap${wide ? ' is-wide' : ''}`}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setShown(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setShown(false); }}
      onFocus={() => { if (pointer.current !== 'touch') setShown(true); }}
      onBlur={(e) => { if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setShown(false); }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && shown) {
          e.preventDefault();
          e.stopPropagation();
          setShown(false);
        }
      }}
    >
      <button
        type="button"
        className={`planner-session-chip is-${session.status.toLowerCase()}`}
        tabIndex={tabIndex}
        draggable={draggable}
        onDragStart={draggable ? onDragStart : undefined}
        aria-describedby={shown ? cardId : undefined}
        onPointerDown={(e) => { pointer.current = e.pointerType; }}
        onKeyDown={(e) => {
          pointer.current = 'keyboard';
          if (e.key === 'Escape' && shown) return;
          onKey?.(e);
        }}
        onClick={() => onOpen(session)}
        data-session={session.id}
      >
        {time && <span className="planner-chip-time">{time}</span>}
        <span className="planner-chip-name">{sessionName(session, t('Session'))}</span>
        <span className="planner-chip-meta">
          <StatusWord status={session.status} />
          {score && <span className="planner-chip-score">{score}</span>}
          <CommentCount n={session.comments} />
        </span>
      </button>
      {shown && (
        <div className={`planner-session-pop${align === 'end' ? ' is-end' : ''}`}>
          <div className="planner-session-card" id={cardId} role="group" aria-label={t('Summary')}>
            <p className="planner-card-title">{sessionName(session, t('Session'))} <StatusWord status={session.status} /></p>
            <SessionDetails session={session} />
            <div className="toolbar">
              <button type="button" className="btn" onClick={() => onOpen(session)}>{t('Open')}</button>
              <MoveForm session={session} onMove={onMove} compact />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SessionCard({ session, onOpen, onEdit, onMove, children }: { session: Session; onOpen: (s: Session) => void; onEdit: (s: Session) => void; onMove: (s: Session, day: string) => Promise<void> | void; children?: ReactNode }) {
  const t = useT();
  return (
    <article className="card planner-session-full">
      <div className="planner-card-head">
        <h3>{sessionName(session, t('Session'))}</h3>
        <StatusWord status={session.status} />
        {sessionScore(session) && <span className="pill">{sessionScore(session)}</span>}
        <CommentCount n={session.comments} />
      </div>
      <SessionDetails session={session} />
      {children}
      <div className="toolbar">
        <button type="button" className="btn" onClick={() => onOpen(session)}>{t('Open')}</button>
        <button type="button" className="btn secondary" onClick={() => onEdit(session)}>{t('Change the plan')}</button>
        <MoveForm session={session} onMove={onMove} />
      </div>
    </article>
  );
}
